import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import PropTypes from 'prop-types';
import { images } from 'utils/icons';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/CancelTwoTone';
import Image from 'components/Image';
import { resize } from 'utils/imageResizer';
import staticText from 'static/text_display.json';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';

const useStyles = makeStyles(() => ({
  input: {
    display: 'none',
  },
  label: {
    borderBottom: 0,
  },
  imageContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    paddingBottom: '1rem',
  },
  container: {
    display: 'flex',
    height: '100%',
    margin: '1rem 1rem 0 0',
    boxSizing: 'border-box',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',
    background: '#222222',
  },
  images: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    margin: '0 !important',
  },
  imagePlaceholder: {
    display: 'flex',
    width: '25%',
    height: '25%',
    margin: '0 auto',
  },
  icon: {
    zIndex: 50000,
    position: 'absolute',
    right: '-10px',
    top: '-10px',
  },
  note: {
    fontSize: '0.75rem',
  },
}));

const MediaUpload = ({
  onChange,
  value,
  label,
  required,
  info,
  maxImageUpload = 9,
}) => {
  const classes = useStyles();

  const handleUpload = evt => {
    const media = {
      mediaType: 'IMAGE', // portal can only support audio at this time
      url: evt,
    };
    if (value) {
      const findUniq = new Set(value.concat(media));
      const array = Array.from(findUniq);
      onChange(array);
    } else {
      onChange([media]);
    }
  };

  const handleCapture = ({ target }) => {
    resize({
      file: target.files[0],
      maxWidth: 600,
      maxHeight: 600,
      fn: handleUpload,
    });
    target.value = '';
  };

  const removeImage = image => {
    const remainingItems = value.filter(item => item !== image);
    onChange(remainingItems);
  };

  const displayAddImageButton =
    !value || (value && value.length < maxImageUpload);

  return (
    <div className={classes.imageContainer}>
      <InputLabel shrink required={required}>
        {label}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <div className={classes.note}>Maximum upload: {maxImageUpload}</div>
      <div className={classes.images}>
        {value &&
          value.map((media, index) => (
            <div className={classes.container} key={index}>
              <label className={classes.label}>
                <Image
                  src={
                    media.mediaType === 'IMAGE'
                      ? media.url
                      : images.audioMedia.default
                  }
                  className={classes.image}
                />
              </label>
              {value && (
                <IconButton
                  color="primary"
                  component="div"
                  className={classes.icon}
                  onClick={() => removeImage(media)}
                  title={
                    media.mediaType === 'IMAGE'
                      ? staticText.general.delete_image
                      : staticText.general.delete_audio
                  }
                >
                  <CancelIcon color="error" />
                </IconButton>
              )}
            </div>
          ))}
        {displayAddImageButton && (
          <div className={classes.container}>
            <label htmlFor={'icon-button-photo'} className={classes.label}>
              <>
                <Image
                  src={images['addMedia'].default}
                  className={classes.imagePlaceholder}
                  title={staticText.general.add_media}
                />
                <input
                  accept="image/*"
                  className={classes.input}
                  id="icon-button-photo"
                  onChange={handleCapture}
                  type="file"
                />
              </>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

MediaUpload.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,

  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  info: InfoPropTypes,
  maxImageUpload: PropTypes.number,
  maxAudioUpload: PropTypes.number,
};

export default MediaUpload;
